import type { LoSiteBlock } from '@/lib/lyrraone-site-blocks';
import { extractVimeoId, extractYouTubeId } from '@/lib/video-block';
import { videoApi } from '@/api/video.api';

export type LoSiteHeroBackgroundType = 'none' | 'image' | 'video';
export type LoSiteHeroBackgroundSize = 'cover' | 'contain';
export type LoSiteHeroBackgroundPosition = 'center' | 'top' | 'bottom';

export interface LoSiteHeroMediaSettings {
  type: LoSiteHeroBackgroundType;
  imageUrl: string;
  videoUrl: string;
  overlay: number;
  size: LoSiteHeroBackgroundSize;
  position: LoSiteHeroBackgroundPosition;
  videoMuted: boolean;
  videoLoop: boolean;
}

export function getLoSiteHeroMedia(block: LoSiteBlock): LoSiteHeroMediaSettings {
  return {
    type: block.heroBackgroundType ?? 'none',
    imageUrl: block.heroBackgroundImageUrl ?? '',
    videoUrl: block.heroBackgroundVideoUrl ?? '',
    overlay: typeof block.heroBackgroundOverlay === 'number' ? block.heroBackgroundOverlay : 40,
    size: block.heroBackgroundSize ?? 'cover',
    position: block.heroBackgroundPosition ?? 'center',
    videoMuted: block.heroVideoMuted !== false,
    videoLoop: block.heroVideoLoop !== false,
  };
}

export function heroHasBackgroundMedia(block: LoSiteBlock): boolean {
  const media = getLoSiteHeroMedia(block);
  return (media.type === 'image' && Boolean(media.imageUrl.trim()))
    || (media.type === 'video' && Boolean(media.videoUrl.trim()));
}

export function heroBackgroundPositionCss(position: LoSiteHeroBackgroundPosition): string {
  if (position === 'top') return 'center top';
  if (position === 'bottom') return 'center bottom';
  return 'center center';
}

export type LoSiteHeroVideoPresentation =
  | { mode: 'native'; src: string }
  | { mode: 'youtube'; embedSrc: string }
  | { mode: 'vimeo'; embedSrc: string };

const YOUTUBE_ID_ONLY = /^[a-zA-Z0-9_-]{11}$/;

function resolveHeroVideoInput(videoUrl: string): string {
  const trimmed = videoUrl.trim();
  if (!trimmed) return '';
  if (trimmed.startsWith('/uploads/')) return videoApi.resolveUrl(trimmed) || trimmed;
  return trimmed;
}

function buildHeroYouTubeEmbed(id: string, opts: { muted: boolean; loop: boolean }): string {
  const params = new URLSearchParams({
    autoplay: '1',
    mute: opts.muted ? '1' : '0',
    controls: '0',
    rel: '0',
    modestbranding: '1',
    playsinline: '1',
    disablekb: '1',
    fs: '0',
    iv_load_policy: '3',
    enablejsapi: '1',
  });
  if (opts.loop) {
    params.set('loop', '1');
    params.set('playlist', id);
  }
  return `https://www.youtube.com/embed/${id}?${params.toString()}`;
}

function buildHeroVimeoEmbed(id: string, opts: { muted: boolean; loop: boolean }): string {
  const params = new URLSearchParams({
    background: '1',
    autoplay: '1',
    muted: opts.muted ? '1' : '0',
    loop: opts.loop ? '1' : '0',
    title: '0',
    byline: '0',
    portrait: '0',
  });
  return `https://player.vimeo.com/video/${id}?${params.toString()}`;
}

/** Résout une URL hero (MP4, YouTube, Vimeo) pour l’arrière-plan plein écran. */
export function getLoSiteHeroVideoPresentation(
  videoUrl: string,
  opts: { muted: boolean; loop: boolean },
): LoSiteHeroVideoPresentation | null {
  const resolved = resolveHeroVideoInput(videoUrl);
  if (!resolved) return null;

  const youtubeId = extractYouTubeId(resolved) ?? (
    YOUTUBE_ID_ONLY.test(resolved.split('?')[0] ?? '') ? resolved.split('?')[0] : null
  );
  if (youtubeId) {
    return { mode: 'youtube', embedSrc: buildHeroYouTubeEmbed(youtubeId, opts) };
  }

  const vimeoId = extractVimeoId(resolved);
  if (vimeoId) {
    return { mode: 'vimeo', embedSrc: buildHeroVimeoEmbed(vimeoId, opts) };
  }

  const src = videoApi.resolveUrl(videoUrl.trim()) || resolved;
  return { mode: 'native', src };
}
